import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  accepterPrixDuDemandeur,
  accepterProposition,
  annoncerBesoin,
  cloturerBesoin,
  createBesoin,
  fetchBesoinCategories,
  fetchBesoinPropositions,
  fetchBesoins,
  modifierPrixBesoin,
  notifierSuperAdminsBesoin,
  proposerUnPrix,
  type Besoin,
  type BesoinCategorie,
  type BesoinInput,
  type BesoinProposition,
} from '@/lib/besoins';
import BesoinCard from '@/pages/dashboard/besoins/components/BesoinCard';
import CreateBesoinModal from '@/pages/dashboard/besoins/components/CreateBesoinModal';
import ProposerModal from '@/pages/dashboard/besoins/components/ProposerModal';
import PropositionsModal from '@/pages/dashboard/besoins/components/PropositionsModal';

// ─────────────────────────────────────────────────────────────
// Besoins — espace partagé entre le tableau de bord commerçant
// (/dashboard/besoins) et le compte client (/mon-compte → Besoins).
// ─────────────────────────────────────────────────────────────

export interface BesoinsUser {
  id: number;
  name?: string | null;
  nomcommerce?: string | null;
  email?: string | null;
  monaie?: string | null;
}

interface Props {
  user: BesoinsUser;
  /** Page des messages à ouvrir depuis une mission attribuée. */
  contactHref?: string;
}

type Tab = 'decouverte' | 'mes-besoins' | 'missions';

const TAB_META: { id: Tab; label: string; icon: string }[] = [
  { id: 'decouverte', label: 'Besoins ouverts', icon: 'ri-compass-3-line' },
  { id: 'mes-besoins', label: 'Mes besoins', icon: 'ri-user-received-2-line' },
  { id: 'missions', label: 'Mes missions', icon: 'ri-briefcase-4-line' },
];

export default function BesoinsPanel({ user, contactHref = '/dashboard/messages' }: Props) {
  const [besoins, setBesoins] = useState<Besoin[]>([]);
  const [propositions, setPropositions] = useState<BesoinProposition[]>([]);
  const [categories, setCategories] = useState<BesoinCategorie[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<Tab>('decouverte');

  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState<Besoin | null>(null);
  const [proposer, setProposer] = useState<Besoin | null>(null);
  const [toast, setToast] = useState('');

  const myId = Number(user.id);
  const authorName = user.nomcommerce || user.name || user.email || 'Un membre Zifek';
  const monaie = user.monaie || 'MAD';

  const showToast = (text: string) => {
    setToast(text);
    window.setTimeout(() => setToast(''), 4500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [rows, cats] = await Promise.all([fetchBesoins(), fetchBesoinCategories()]);
      setCategories(cats);
      // Visible : mes besoins, les besoins ouverts, et ceux qui m'ont été attribués.
      const visible = rows.filter(
        (b) => b.user_id === myId || b.professionnel_id === myId || b.statut === 'ouvert',
      );
      setBesoins(visible);
      const props = await fetchBesoinPropositions(visible.map((b) => b.id));
      setPropositions(props);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les besoins.');
      setBesoins([]);
      setPropositions([]);
    } finally {
      setLoading(false);
    }
  }, [myId]);

  useEffect(() => {
    void load();
  }, [load]);

  const propsOf = useCallback(
    (besoinId: number) => propositions.filter((p) => p.besoin_id === besoinId),
    [propositions],
  );

  const lists = useMemo(() => {
    const decouverte = besoins.filter((b) => b.statut === 'ouvert' && b.user_id !== myId);
    const mesBesoins = besoins
      .filter((b) => b.user_id === myId)
      .sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
    const missions = besoins.filter((b) => b.professionnel_id === myId && b.user_id !== myId);
    return { decouverte, mesBesoins, missions };
  }, [besoins, myId]);

  const current = tab === 'decouverte' ? lists.decouverte : tab === 'mes-besoins' ? lists.mesBesoins : lists.missions;

  const handleCreate = async (input: BesoinInput) => {
    const created = await createBesoin(myId, authorName, input);
    setCreateOpen(false);
    setTab('mes-besoins');
    showToast('Besoin créé. Cliquez sur « Annoncer » pour notifier les professionnels.');
    void notifierSuperAdminsBesoin(created, `Nouveau besoin publié : ${created.titre} (par ${authorName}).`);
    await load();
    setDetail(created);
  };

  const handleAnnoncer = async (besoin: Besoin) => {
    const result = await annoncerBesoin(besoin);
    showToast(result.message);
    await load();
  };

  const handleAcceptBudget = async (besoin: Besoin) => {
    await accepterPrixDuDemandeur(besoin, myId, authorName);
    setProposer(null);
    showToast('Mission acceptée ! Le demandeur a été prévenu.');
    await load();
  };

  const handlePropose = async (besoin: Besoin, prix: number, message: string) => {
    await proposerUnPrix(besoin, myId, authorName, prix, message);
    setProposer(null);
    showToast('Votre proposition a été envoyée au demandeur.');
    await load();
  };

  const handleAcceptProposition = async (besoin: Besoin, proposition: BesoinProposition) => {
    await accepterProposition(besoin, proposition);
    showToast('Proposition retenue : la mission est attribuée.');
    await load();
    setDetail(null);
  };

  const handleUpdateBudget = async (besoin: Besoin, budget: number | null, monaie: string) => {
    await modifierPrixBesoin(besoin, budget, monaie);
    setDetail({ ...besoin, budget, monaie });
    showToast('Nouveau prix enregistré et professionnels prévenus.');
    await load();
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Besoins</h2>
          <p className="text-sm text-foreground-500 mt-1">
            Publiez un besoin, recevez des propositions de prix et choisissez le professionnel qui vous convient.
          </p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors w-fit"
        >
          <i className="ri-add-line"></i>
          Créer un besoin
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-1 p-1 bg-background-100 border border-background-200/60 rounded-full w-fit mb-5">
        {TAB_META.map((item) => {
          const count = item.id === 'decouverte'
            ? lists.decouverte.length
            : item.id === 'mes-besoins'
              ? lists.mesBesoins.length
              : lists.missions.length;
          return (
            <button
              key={item.id}
              onClick={() => setTab(item.id)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                tab === item.id ? 'bg-background-50 text-foreground-950' : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              <i className={item.icon}></i>
              {item.label}
              <span className={`${tab === item.id ? 'text-foreground-500' : 'text-foreground-400'}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <i className="ri-error-warning-line mt-0.5"></i>
          <span className="flex-1">{error}</span>
          <button onClick={() => void load()} className="underline font-medium whitespace-nowrap cursor-pointer">Réessayer</button>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          <p className="text-xs text-foreground-400 mt-3">Chargement des besoins...</p>
        </div>
      ) : current.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/60 rounded-lg px-6 text-center">
          <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-hand-heart-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-sm font-semibold text-foreground-800">
            {tab === 'decouverte' ? 'Aucun besoin ouvert pour le moment' : tab === 'mes-besoins' ? 'Vous n’avez publié aucun besoin' : 'Aucune mission pour le moment'}
          </p>
          <p className="text-xs text-foreground-500 mt-1 max-w-md">
            {tab === 'decouverte'
              ? 'Les besoins publiés par les autres membres de Zifek apparaîtront ici dès leur annonce.'
              : tab === 'mes-besoins'
                ? 'Décrivez votre besoin en quelques lignes : les professionnels concernés seront notifiés.'
                : 'Quand un de vos prix sera retenu, la mission apparaîtra ici avec les coordonnées du demandeur.'}
          </p>
          {tab !== 'decouverte' && (
            <button
              onClick={() => setCreateOpen(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              <i className="ri-add-line"></i>
              Créer un besoin
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {current.map((besoin) => {
            const isMine = besoin.user_id === myId;
            const isWinner = besoin.professionnel_id === myId;
            return (
              <BesoinCard
                key={besoin.id}
                besoin={besoin}
                isMine={isMine}
                isWinner={isWinner}
                propositionsCount={propsOf(besoin.id).length}
                myProposition={propsOf(besoin.id).some((p) => p.user_id === myId)}
                onOpenDetail={() => setDetail(besoin)}
                onProposer={() => setProposer(besoin)}
              />
            );
          })}
        </div>
      )}

      {createOpen && (
        <CreateBesoinModal
          categories={categories}
          monaie={monaie}
          onClose={() => setCreateOpen(false)}
          onSubmit={handleCreate}
        />
      )}

      {proposer && (
        <ProposerModal
          besoin={proposer}
          onClose={() => setProposer(null)}
          onAcceptBudget={() => handleAcceptBudget(proposer)}
          onPropose={(prix, message) => handlePropose(proposer, prix, message)}
        />
      )}

      {detail && (
        <PropositionsModal
          besoin={detail}
          propositions={propsOf(detail.id)}
          contactHref={contactHref}
          onClose={() => setDetail(null)}
          onAccept={(proposition) => handleAcceptProposition(detail, proposition)}
          onUpdateBudget={(budget, devise) => handleUpdateBudget(detail, budget, devise)}
          onAnnoncer={() => handleAnnoncer(detail)}
          onCloturer={async () => {
            await cloturerBesoin(detail.id);
            setDetail(null);
            showToast('Besoin clôturé.');
            await load();
          }}
        />
      )}

      {toast && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[60] w-[calc(100vw-2rem)] max-w-md">
          <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-foreground-900 text-background-50 text-xs font-medium">
            <i className="ri-information-line mt-0.5"></i>
            <span className="flex-1">{toast}</span>
          </div>
        </div>
      )}
    </div>
  );
}